import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Show, SignIn, SignUp } from "@clerk/react";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { Toaster } from "@/components/ui/sonner";
import { AppProvider, useApp } from "@/lib/app-context";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import Inventory from "@/pages/Inventory";
import Purchases from "@/pages/Purchases";
import POS from "@/pages/POS";
import Invoices from "@/pages/Invoices";
import Orders from "@/pages/Orders";
import Labels from "@/pages/Labels";
import Zepio from "@/pages/Zepio";
import Reports from "@/pages/Reports";
import SettingsPage from "@/pages/Settings";
import Users from "@/pages/Users";
import Catalogue from "@/pages/Catalogue";
import StockStatus from "@/pages/StockStatus";
import { UserButton } from "@clerk/react";

const AuthPage = ({ children }) => (
  <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
    <div className="hidden lg:flex flex-col justify-between p-12 bg-emerald-900 text-white relative overflow-hidden">
      <img src="https://images.pexels.com/photos/9705821/pexels-photo-9705821.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
      <div className="relative font-display text-2xl font-extrabold tracking-tight">Zilabi<span className="text-emerald-400">.</span>ERP</div>
      <div className="relative">
        <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight">Every shelf, batch and bill<br />in one place.</h1>
        <p className="mt-4 text-slate-300 max-w-md">Inventory, POS, purchasing and Zepio online-store sync for Zilabi Grocery & Essentials.</p>
      </div>
      <div className="relative text-xs text-slate-400">Staff access only</div>
    </div>
    <div className="flex items-center justify-center p-4">{children}</div>
  </div>
);

const Gate = () => {
  const { me, error } = useApp();
  if (error) return <div className="p-10 text-sm text-red-600" data-testid="app-load-error">Could not load your account. Refresh to retry.</div>;
  if (!me) return <div className="p-10 text-sm text-muted-foreground" data-testid="app-loading">Loading Zilabi ERP…</div>;
  if (me.role === "pending")
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center" data-testid="pending-approval">
        <UserButton />
        <h1 className="text-2xl font-bold">Waiting for approval</h1>
        <p className="text-muted-foreground max-w-md">Your account ({me.email}) is signed in. An administrator must assign you a role before you can use the ERP.</p>
      </div>
    );
  return <Layout><Outlet /></Layout>;
};

const Protected = () => (
  <>
    <Show when="signed-in"><AppProvider><Gate /></AppProvider></Show>
    <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
  </>
);

function App() {
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <Routes>
          <Route path="/sign-in/*" element={<AuthPage><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthPage>} />
          <Route path="/sign-up/*" element={<AuthPage><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthPage>} />
          <Route element={<Protected />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/pos" element={<POS />} />
            <Route path="/products" element={<Products />} />
            <Route path="/catalogue" element={<Catalogue />} />
            <Route path="/out-of-stock" element={<StockStatus kind="out" key="out" />} />
            <Route path="/low-stock" element={<StockStatus kind="low" key="low" />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/purchases" element={<Purchases />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/labels" element={<Labels />} />
            <Route path="/zepio" element={<Zepio />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/users" element={<Users />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
        <Toaster richColors position="top-right" />
      </ClerkAuthProvider>
    </BrowserRouter>
  );
}

export default App;
